import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useCompliancePackStore } from '../../stores/compliancePackStore';
import { StubCard } from './StubCard';
import { projectFileUrl } from '@/lib/api';
import { runFolder } from '@/lib/format';

export function StubTriageList() {
  const { projectId, runId: runIdParam } = useParams<{
    projectId: string
    runId?: string
  }>()
  const runId = Number(runIdParam) || 1

  const pack = useCompliancePackStore(state => state.pack);
  const setPack = useCompliancePackStore(state => state.setPack);
  const [loading, setLoading] = useState(true);

  // Always (re)load the pack when the project / run changes. The previous
  // version short-circuited if `pack` was non-null in the zustand store,
  // which meant that navigating from Project A to Project B (or even
  // re-opening Project A after a re-emit of phase 2.5) showed the
  // previous run's pack — including its already-confirmed stubs — with
  // no way for the architect to edit them. We now key the load on
  // (projectId, runId) and write the freshly fetched pack into the
  // store every time, so the surface always reflects what's on disk.
  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    setLoading(true);
    async function load() {
      try {
        const res = await fetch(
          projectFileUrl(
            projectId!,
            `${runFolder(runId)}/phase2_5/compliance-requirement-pack.json`,
          ),
        );
        if (res.ok && !cancelled) {
          const data = await res.json();
          setPack(data);
        } else if (!cancelled) {
          // Drop any stale pack belonging to a different project/run so
          // the architect doesn't see the previous project's confirmed
          // stubs while the new project's compliance phase is still
          // running.
          setPack(null);

        }
      } catch (e) {
        console.error(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [projectId, runId, setPack]);

  if (loading) {
    return <div className="p-4 text-slate-300">Loading compliance pack…</div>;
  }

  if (!pack) {
    return (
      <div className="p-4 text-slate-400">
        No compliance pack generated. Please ensure Phase 2 has completed and
        compliance cues are selected.
      </div>
    );
  }
  
  const stubs = pack.stubs || [];
  const cues = pack.cues || [];
  
  const pendingCount = stubs.filter(s => s.status === 'pending' || s.status === 'drafted').length;
  const confirmedCount = stubs.filter(s => s.status === 'user_confirmed').length;
  const droppedCount = stubs.filter(s => s.status === 'dropped').length;

  return (
    <div className="flex flex-col h-full">
      <div className="bg-slate-900/60 border-b border-slate-700/50 p-4">
        <h3 className="font-bold text-lg mb-2 text-slate-100">Compliance Interview</h3>
        <p className="text-sm text-slate-300 mb-2">
          Based on <strong>{cues.join(', ')}</strong>, CARBON has injected{' '}
          {stubs.length} system requirements. Review each one — the swarm uses
          your <em>confirmed</em> stubs verbatim, and any you <em>drop</em> will
          be logged in the compliance deferral appendix.
        </p>
        <div className="flex space-x-4 text-sm font-medium">
          <span className="text-emerald-400">{confirmedCount} Confirmed</span>
          <span className="text-amber-400">{pendingCount} Pending</span>
          <span className="text-slate-400">{droppedCount} Dropped</span>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {stubs.map(stub => (
          <StubCard key={stub.stub_id} stub={stub} />
        ))}
      </div>
    </div>
  );
}
